<template>
  <NotificationTemplate
    :notification="notification"
    class="notification__platform-load-metrics"
  >
    <template #pic>
      <icon
        :data="require(!isDark ? '@icon/waterline.svg' : '@icon/waterline.dark.svg')"
        original
        width="var(--icon-24)"
        height="var(--icon-24)"
        class="notification__platform-load-metrics__icon"
      />
    </template>

    <template #title>
      <BaseSubHeading
        size="large"
        color="monochrome-06"
        darkColor="monochrome-03"
        weight="500"
        class="notification__platform-load-metrics__title"
      >
        {{ $t("notifications.platformLoadMetricsExceeded.title") }}
      </BaseSubHeading>
    </template>

    <template #content>
      <div
        v-for="metric in metrics"
        :key="metric.key"
        class="notification__platform-load-metrics-line"
      >
        <BaseSubHeading
          size="medium"
          color="monochrome-07"
          darkColor="monochrome-03"
          weight="500"
          class="notification__platform-load-metrics-label"
        >
          {{ $t(`notifications.platformLoadMetricsExceeded.metrics.${metric.key}`) }}
        </BaseSubHeading>

        <div class="notification__platform-load-metrics-counts">
          <BaseSubHeading
            size="medium"
            weight="600"
            class="notification__platform-load-metrics__current"
            :class="{ 'is-exceeded': metric.counts.current > metric.counts.max }"
          >
            {{ metric.counts.current }}
          </BaseSubHeading>
          <BaseSubHeading
            size="medium"
            color="monochrome-07"
            darkColor="monochrome-03"
            weight="400"
          >
            {{ $t("notifications.platformLoadMetricsExceeded.of") }}
          </BaseSubHeading>
          <BaseSubHeading
            size="medium"
            weight="600"
            class="notification__platform-load-metrics__max"
            :class="{ 'is-exceeded': metric.counts.current > metric.counts.max }"
          >
            {{ metric.counts.max }}
          </BaseSubHeading>
        </div>
      </div>
    </template>
  </NotificationTemplate>
</template>

<script setup lang="ts">
/**
 * Displays current platform-load metrics against project limits from the backend notification payload.
 */
import { computed } from "vue"
import { useStore } from "vuex"

import NotificationTemplate from "@/components/Notification/NotificationTemplate.vue"

import { NotificationPlatformLoadMetrics } from "@/models/Notification"
import { Getters } from "@/store"

const props = defineProps<{
  notification: NotificationPlatformLoadMetrics
}>()

const store = useStore()
const isDark = computed<boolean>(() => store.getters[Getters.THEME_IS_DARK])

type MetricKey = "incidents" | "reliability" | "security" | "performance"

const metricKeys: MetricKey[] = ["incidents", "reliability", "security", "performance"]
const metrics = computed(() =>
  metricKeys.map(key => ({
    key,
    counts: props.notification.content[key]
  }))
)
</script>
